/*
 * JC header (sections/jc-header.liquid).
 * - Writes --header-height (the bar's height) for the sticky unit and anchor offsets, and adds
 *   .jc-hdr-scrolled to the section once the page has scrolled (soft shadow under the bar).
 * - Desktop mega menus (Men, Women): the label is a link to the page (mouse click or Enter goes
 *   there) and opens the menu on hover after 80ms. With a menu already open, moving to the other
 *   label swaps at once (no fade, no flicker). Leaving both the label and its panel closes after
 *   200ms; coming back cancels. On touch, the first tap on the label opens the menu and a second
 *   tap goes to the page. The chevron button toggles the menu (click, Enter, Space; from the
 *   keyboard, focus moves to the first link; ArrowDown does the same). Hovering a plain link
 *   (Shop all, Best sellers), Esc, a click outside, or focus leaving closes it at once.
 * - Mobile drawer: slides in from the left; overlay tap, Esc and the X close it; page scroll is
 *   locked and focus is kept inside while it is open; focus returns to the menu button.
 *   Men / Women tabs (arrow keys move between them) and one-open-at-a-time accordions.
 */
if (!customElements.get('jc-header')) {
  class JcHeader extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('.section-header') || this.parentElement;
      this.bar = this.querySelector('.jc-hdr__bar');
      this.desktop = window.matchMedia('(min-width: 990px)');
      this.fine = window.matchMedia('(hover: hover) and (pointer: fine)');
      this.setHeight();
      if (window.ResizeObserver) new ResizeObserver(() => this.setHeight()).observe(this.bar);
      this.onScroll = () => this.section.classList.toggle('jc-hdr-scrolled', window.scrollY > 2);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.onScroll();
      this.initMega();
      this.initDrawer();
    }

    setHeight() {
      document.documentElement.style.setProperty('--header-height', `${this.bar.offsetHeight}px`);
    }

    /* ---------- desktop mega menus ---------- */
    initMega() {
      this.items = [...this.querySelectorAll('[data-jc-mega-item]')]
        .map((el) => {
          const btn = el.querySelector('[data-jc-mega-trigger]');
          return { el, btn, label: el.querySelector('[data-jc-mega-label]'), panel: btn && document.getElementById(btn.getAttribute('aria-controls')) };
        })
        .filter((it) => it.btn && it.panel);
      if (!this.items.length) return;
      this.openItem = null;
      this.keyboard = false;
      this.pointer = null;
      document.addEventListener(
        'keydown',
        () => {
          this.keyboard = true;
          this.pointer = null;
        },
        true
      );
      document.addEventListener(
        'pointerdown',
        (e) => {
          this.keyboard = false;
          this.pointer = e.pointerType;
        },
        true
      );

      this.items.forEach((it) => {
        const { el, btn, label, panel } = it;
        // mouse: hovering the label or the chevron opens after ~120ms (hover intent); when a menu
        // is already open, the swap is instant
        el.addEventListener('pointerenter', (e) => {
          if (e.pointerType !== 'mouse' || !this.desktop.matches) return;
          // while the search panel is open, passing over the nav must not swap it for a menu
          if (this.section.classList.contains('jc-hdr-search-open')) return;
          clearTimeout(this.closeTimer);
          clearTimeout(this.openTimer);
          if (this.openItem) this.open(it);
          else this.openTimer = setTimeout(() => this.open(it), 80);
        });
        // leaving the label or its panel: close after 200ms unless the pointer comes back to either
        const leave = (e) => {
          if (e.pointerType !== 'mouse') return;
          clearTimeout(this.openTimer);
          if (this.openItem !== it) return;
          const to = e.relatedTarget;
          if (to && (el.contains(to) || panel.contains(to))) return;
          clearTimeout(this.closeTimer);
          this.closeTimer = setTimeout(() => this.close(), 200);
        };
        el.addEventListener('pointerleave', leave);
        panel.addEventListener('pointerleave', leave);
        panel.addEventListener('pointerenter', () => clearTimeout(this.closeTimer));
        // the label is a real link: a mouse click or Enter goes to the page. On touch (no hover),
        // the first tap opens the dropdown and a second tap on the label goes to the page.
        if (label) {
          label.addEventListener('click', (e) => {
            const touch = this.pointer === 'touch' || this.pointer === 'pen';
            if (touch && this.desktop.matches && this.openItem !== it) {
              e.preventDefault();
              this.open(it);
            }
          });
        }
        // the chevron button toggles the dropdown (click, tap, Enter, Space); ArrowDown opens it
        // and moves to the first link
        btn.addEventListener('click', () => {
          if (this.openItem === it) {
            this.close();
            return;
          }
          this.open(it);
          // Enter / Space: focus moves into the panel
          if (this.keyboard) {
            const first = panel.querySelector('a[href]');
            if (first) requestAnimationFrame(() => first.focus());
          }
        });
        btn.addEventListener('keydown', (e) => {
          const first = panel.querySelector('a[href]');
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            this.open(it);
            if (first) requestAnimationFrame(() => first.focus());
          } else if (e.key === 'Tab' && !e.shiftKey && this.openItem === it && first) {
            // Tab from an open chevron goes into its dropdown
            e.preventDefault();
            first.focus();
          }
        });
        // past the dropdown's last link, Tab goes on to whatever follows the chevron in the nav;
        // Shift+Tab from the first link comes back to the chevron
        panel.addEventListener('keydown', (e) => {
          if (e.key !== 'Tab') return;
          const links = [...panel.querySelectorAll('a[href]')];
          const i = links.indexOf(document.activeElement);
          if (e.shiftKey && i === 0) {
            e.preventDefault();
            btn.focus();
          } else if (!e.shiftKey && i === links.length - 1) {
            e.preventDefault();
            const nav = [...this.querySelectorAll('.jc-hdr__nav-list a[href], .jc-hdr__nav-list button')];
            const next = nav[nav.indexOf(btn) + 1];
            this.close();
            (next || this.querySelector('.jc-hdr__logo'))?.focus();
          }
        });
      });

      // plain links (Shop all, Best sellers) close any open panel straight away
      this.querySelectorAll('[data-jc-nav-plain]').forEach((a) =>
        a.addEventListener('pointerenter', (e) => {
          if (e.pointerType !== 'mouse') return;
          clearTimeout(this.openTimer);
          this.close();
        })
      );
      document.addEventListener('click', (e) => {
        if (this.openItem && !this.contains(e.target)) this.close();
      });
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.openItem) {
          const { btn } = this.openItem;
          const inside = this.contains(document.activeElement);
          this.close();
          if (inside) btn.focus();
        }
      });
      // keyboard: close once focus has left the open dropdown and its label / chevron
      this.addEventListener('focusout', () => {
        if (!this.openItem || !this.keyboard) return;
        setTimeout(() => {
          const it = this.openItem;
          const a = document.activeElement;
          if (!it || it.panel.contains(a) || it.el.contains(a)) return;
          this.close();
        }, 0);
      });
      this.desktop.addEventListener('change', () => this.close());
    }

    open(it) {
      clearTimeout(this.closeTimer);
      if (this.openItem === it) return;
      const search = this.querySelector('jc-search');
      if (search && search.isOpen) search.close(true);
      if (this.openItem) {
        // swap: no fade either way, so the two panels never show at once
        this.classList.add('is-swapping');
        this.setOpen(this.openItem, false);
        this.setOpen(it, true);
        this.openItem = it;
        requestAnimationFrame(() => requestAnimationFrame(() => this.classList.remove('is-swapping')));
        return;
      }
      this.setOpen(it, true);
      this.openItem = it;
      this.section.classList.add('jc-hdr-mm-open');
    }

    close() {
      clearTimeout(this.openTimer);
      clearTimeout(this.closeTimer);
      if (this.openItem) this.setOpen(this.openItem, false);
      this.openItem = null;
      this.section.classList.remove('jc-hdr-mm-open');
    }

    setOpen(it, on) {
      it.panel.classList.toggle('is-open', on);
      it.el.classList.toggle('is-open', on);
      it.btn.setAttribute('aria-expanded', on ? 'true' : 'false');
    }

    /* ---------- mobile drawer ---------- */
    initDrawer() {
      this.drawer = this.querySelector('[data-jc-drawer]');
      this.menuBtn = this.querySelector('[data-jc-drawer-open]');
      if (!this.drawer || !this.menuBtn) return;
      this.panel = this.drawer.querySelector('.jc-drawer__panel');
      this.drawer.setAttribute('aria-hidden', 'true');
      this.drawer.inert = true;

      this.menuBtn.addEventListener('click', () => this.openDrawer());
      this.drawer.querySelectorAll('[data-jc-drawer-close]').forEach((el) => el.addEventListener('click', () => this.closeDrawer()));
      this.drawer.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          e.stopPropagation();
          this.closeDrawer();
        } else if (e.key === 'Tab') this.trap(e);
      });
      this.desktop.addEventListener('change', () => this.desktop.matches && this.closeDrawer(true));

      // tabs
      const tabs = [...this.drawer.querySelectorAll('[data-jc-tab]')];
      const select = (tab, focus) => {
        tabs.forEach((t) => {
          const on = t === tab;
          t.setAttribute('aria-selected', on ? 'true' : 'false');
          t.tabIndex = on ? 0 : -1;
          const p = document.getElementById(t.getAttribute('aria-controls'));
          if (p) p.hidden = !on;
        });
        if (focus) tab.focus();
      };
      tabs.forEach((t, i) => {
        t.addEventListener('click', () => select(t));
        t.addEventListener('keydown', (e) => {
          if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
          e.preventDefault();
          select(tabs[(i + (e.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length], true);
        });
      });

      // accordions: one open at a time within a tab
      this.drawer.querySelectorAll('[data-jc-acc]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const open = btn.getAttribute('aria-expanded') !== 'true';
          const list = btn.closest('.jc-acc');
          list.querySelectorAll('[data-jc-acc]').forEach((b) => {
            const on = b === btn && open;
            b.setAttribute('aria-expanded', on ? 'true' : 'false');
            const p = document.getElementById(b.getAttribute('aria-controls'));
            if (p) p.classList.toggle('is-open', on);
          });
        });
      });
    }

    openDrawer() {
      if (this.drawer.classList.contains('is-open')) return;
      this.drawer.inert = false;
      this.drawer.setAttribute('aria-hidden', 'false');
      this.drawer.classList.add('is-open');
      this.menuBtn.setAttribute('aria-expanded', 'true');
      document.documentElement.classList.add('jc-scroll-lock');
      const first = this.drawer.querySelector('.jc-drawer__close');
      requestAnimationFrame(() => (first || this.panel).focus({ preventScroll: true }));
    }

    closeDrawer(silent) {
      if (!this.drawer.classList.contains('is-open')) return;
      this.drawer.classList.remove('is-open');
      this.drawer.setAttribute('aria-hidden', 'true');
      this.drawer.inert = true;
      this.menuBtn.setAttribute('aria-expanded', 'false');
      document.documentElement.classList.remove('jc-scroll-lock');
      if (!silent) this.menuBtn.focus({ preventScroll: true });
    }

    trap(e) {
      const items = [...this.panel.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])')].filter(
        (el) => el.offsetParent !== null && !el.closest('[hidden]') && !el.closest('.jc-acc__panel:not(.is-open)')
      );
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }
  customElements.define('jc-header', JcHeader);
}
